// Sugestii sub câmpul de căutare: aceeași monedă pe toate bursele, dintr-o privire.
//
// Diferența față de căutarea globală (cea care se deschide când scrii oriunde) e că
// aceasta arată **unde** se tranzacționează instrumentul: fiecare rând poartă bursa și
// piața, grupate în perpetue și spot. Când cauți „btc" nu vrei doar BTCUSDT, vrei să
// vezi pe ce bursă are sens să-l deschizi.
//
// Căutarea se face pe server: el are deja agregatele tuturor burselor în memorie, deci
// un singur apel în loc de douăsprezece din browser.

import { el, clear, debounce, fmtPrice, fmtVolume } from '../core/util.js';
import { iconMoneda } from './coin-icon.js';
import { baseTicker } from './components.js';

/** Culorile de marcă, ca bursa să se recunoască din periferie. */
const CULORI = {
  binance: '#F0B90B',
  bybit: '#F7A600',
  okx: '#9AA4B2',
  bitget: '#00CFC1',
  gate: '#3A6BE8',
  mexc: '#1EA0F0',
};

const GRUPE = [
  { market: 'futures', titlu: 'Фьючерсы', eticheta: 'PERP' },
  { market: 'spot', titlu: 'Спот', eticheta: 'SPOT' },
];

/**
 * Leagă o listă de sugestii la un câmp de căutare.
 *
 * @param {HTMLInputElement} input
 * @param {(item) => void} onPick  ce facem cu instrumentul ales
 * @returns {() => void} funcție de desfacere
 */
export function attachSearchDropdown(input, onPick) {
  let items = [];
  let index = 0;
  let box = null;

  const inchide = () => { box?.remove(); box = null; items = []; index = 0; };

  const deschide = () => {
    if (box) return;
    box = el('div', { class: 'sdrop' });
    // Ancorat la `body`, nu la părinte: câmpurile de căutare stau în bare cu
    // `overflow: hidden`, care ar tăia lista exact când e mai lungă.
    document.body.append(box);
    pozitioneaza();
  };

  const pozitioneaza = () => {
    if (!box) return;
    const r = input.getBoundingClientRect();
    box.style.left = `${r.left}px`;
    box.style.top = `${r.bottom + 4}px`;
    box.style.width = `${Math.max(r.width, 320)}px`;
  };

  const randeaza = () => {
    if (!items.length) { inchide(); return; }
    deschide();
    clear(box);

    let i = 0;
    for (const grup of GRUPE) {
      const aleGrupului = items.filter((x) => x.market === grup.market);
      if (!aleGrupului.length) continue;
      box.append(el('div', { class: 'sdrop-group', text: grup.titlu }));

      for (const item of aleGrupului) {
        const pozitie = i++;
        box.append(el('button', {
          class: `sdrop-row${pozitie === index ? ' active' : ''}`,
          dataset: { i: String(pozitie) },
          // `mousemove`, nu `mouseenter`: lista apare sub cursorul nemișcat și ar muta
          // singură selecția pe rândul de dedesubt.
          onmousemove: () => { if (index !== pozitie) { index = pozitie; sincronizeaza(); } },
          onmousedown: (e) => { e.preventDefault(); alege(item); },
        }, [
          iconMoneda(item.symbol, 17),
          el('span', { class: 'sdrop-sym', text: baseTicker(item.symbol) }),
          el('span', {
            class: 'sdrop-ex',
            text: item.exchange.toUpperCase(),
            style: { color: CULORI[item.exchange] || 'var(--text-dim)',
                     borderColor: CULORI[item.exchange] || 'var(--border-2)' },
          }),
          el('span', { class: 'sdrop-mkt', text: grup.eticheta }),
          el('span', { class: 'sdrop-vol', text: fmtVolume(item.quoteVolume) }),
          el('span', { class: 'sdrop-price', text: fmtPrice(item.price) }),
        ]));
      }
    }
  };

  const sincronizeaza = () => {
    box?.querySelectorAll('.sdrop-row').forEach((r) => {
      r.classList.toggle('active', Number(r.dataset.i) === index);
    });
    box?.querySelector('.sdrop-row.active')?.scrollIntoView({ block: 'nearest' });
  };

  const alege = (item) => {
    inchide();
    onPick?.(item);
  };

  const cauta = debounce(async () => {
    const q = input.value.trim();
    if (q.length < 2) { inchide(); return; }
    try {
      const data = await (await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=30`)).json();
      items = data.items || [];
      index = 0;
      randeaza();
    } catch {
      inchide();
    }
  }, 160);

  const onInput = () => cauta();
  const onKey = (e) => {
    if (!items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      index = (index + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length;
      sincronizeaza();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      alege(items[index]);
    } else if (e.key === 'Escape') {
      inchide();
    }
  };
  const onBlur = () => setTimeout(inchide, 120);

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKey);
  input.addEventListener('blur', onBlur);
  window.addEventListener('scroll', pozitioneaza, true);
  window.addEventListener('resize', pozitioneaza);

  return () => {
    inchide();
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKey);
    input.removeEventListener('blur', onBlur);
    window.removeEventListener('scroll', pozitioneaza, true);
    window.removeEventListener('resize', pozitioneaza);
  };
}
